import type { Building } from '@shared/models/design/building-design.model';
import type { Point2D } from '@shared/models/geometry.model';
import { doPolygonsOverlapInterior } from '../geometry/geometry.helpers';
import { getBuildingSolidShapesOverlappingElevation } from './shape.helpers';
import type { ElevationRange } from './storey.helpers';

const OVERLAP_CLAMP_BINARY_STEPS = 20;
const MIN_EDGE_LENGTH = 0.001;
const SEPARATION_SEARCH_METERS = 200;

export type TranslationOverlapObstacle = {
  points: readonly Point2D[];
};

export type MovingPolygonForOverlapClamp = {
  points: readonly Point2D[];
  obstacles: readonly TranslationOverlapObstacle[];
};

export type ClampTranslationDeltaOutsideObstaclesInput = {
  moving: readonly MovingPolygonForOverlapClamp[];
  delta: Point2D;
};

export type MovingSolidForOverlapClamp = {
  points: readonly Point2D[];
  range: ElevationRange;
};

export type ClampTranslationDeltaOutsideOtherBuildingsInput = {
  movingSolids: readonly MovingSolidForOverlapClamp[];
  otherBuildings: readonly Building[];
  delta: Point2D;
};

/**
 * Clamp a drag translation so moving polygons stay outside their obstacles.
 *
 * Used both during live drag and on mouseup. The mouse (desired) pose wins:
 * a clear destination is kept. An overlapping destination is pushed out along
 * the shortest separating translation — the side nearest the desired pose, not
 * the drag origin.
 */
export const clampTranslationDeltaOutsideObstacles = (
  input: ClampTranslationDeltaOutsideObstaclesInput,
): Point2D => {
  const { moving, delta } = input;
  const [dx, dy] = delta;
  if (!dx && !dy) return delta;
  if (!moving.length) return delta;
  if (moving.every((item) => !item.obstacles.length)) return delta;

  const intersects = (tx: number, ty: number): boolean =>
    movingPolygonsOverlapObstacles(moving, tx, ty);

  if (!intersects(dx, dy)) return delta;

  const extra = nearestSeparationDelta(
    (tx, ty) => intersects(dx + tx, dy + ty),
    moving,
  );
  return [dx + extra[0], dy + extra[1]];
};

/**
 * Clamp a drag translation so moving solids stay outside sibling buildings.
 * Delegates to {@link clampTranslationDeltaOutsideObstacles}.
 */
export const clampTranslationDeltaOutsideOtherBuildings = (
  input: ClampTranslationDeltaOutsideOtherBuildingsInput,
): Point2D => {
  const { movingSolids, otherBuildings, delta } = input;
  if (!movingSolids.length || !otherBuildings.length) return delta;
  const buildings = [...otherBuildings];
  return clampTranslationDeltaOutsideObstacles({
    delta,
    moving: movingSolids.map((solid) => ({
      points: solid.points,
      obstacles: getBuildingSolidShapesOverlappingElevation(
        buildings,
        solid.range,
      ).map((shape) => ({ points: shape.points })),
    })),
  });
};

/** True when any translated polygon shares interior area with its obstacles. */
const movingPolygonsOverlapObstacles = (
  moving: readonly MovingPolygonForOverlapClamp[],
  dx: number,
  dy: number,
): boolean =>
  moving.some((item) => {
    if (item.points.length < 3) return false;
    const translated = item.points.map(([x, y]): Point2D => [x + dx, y + dy]);
    return item.obstacles.some((obstacle) =>
      doPolygonsOverlapInterior(translated, obstacle.points),
    );
  });

/**
 * Smallest extra translation that separates polygons from their obstacles.
 * Tries cardinal axes and obstacle edge normals so the nearest side wins.
 */
const nearestSeparationDelta = (
  intersects: (dx: number, dy: number) => boolean,
  moving: readonly MovingPolygonForOverlapClamp[],
): Point2D => {
  const dirs: Point2D[] = [
    [SEPARATION_SEARCH_METERS, 0],
    [-SEPARATION_SEARCH_METERS, 0],
    [0, SEPARATION_SEARCH_METERS],
    [0, -SEPARATION_SEARCH_METERS],
  ];
  for (const item of moving) {
    for (const obstacle of item.obstacles) {
      const points = obstacle.points;
      if (points.length < 2) continue;
      for (let i = 0; i < points.length; i += 1) {
        const start = points[i];
        const end = points[(i + 1) % points.length];
        if (!start || !end) continue;
        const edgeDx = end[0] - start[0];
        const edgeDy = end[1] - start[1];
        const edgeLen = Math.hypot(edgeDx, edgeDy);
        if (edgeLen < MIN_EDGE_LENGTH) continue;
        const nx = -edgeDy / edgeLen;
        const ny = edgeDx / edgeLen;
        dirs.push(
          [nx * SEPARATION_SEARCH_METERS, ny * SEPARATION_SEARCH_METERS],
          [-nx * SEPARATION_SEARCH_METERS, -ny * SEPARATION_SEARCH_METERS],
        );
      }
    }
  }

  let best: Point2D = [0, 0];
  let bestDist = Infinity;
  for (const [dirX, dirY] of dirs) {
    const exit = firstSeparationAlong(intersects, dirX, dirY);
    if (!exit) continue;
    const dist = exit[0] * exit[0] + exit[1] * exit[1];
    if (dist < bestDist) {
      bestDist = dist;
      best = exit;
    }
  }
  return best;
};

/** Smallest t ∈ (0, 1] along delta where the polygons no longer overlap. */
const firstSeparationAlong = (
  intersects: (dx: number, dy: number) => boolean,
  dx: number,
  dy: number,
): Point2D | undefined => {
  if (intersects(dx, dy)) return undefined;
  let low = 0;
  let high = 1;
  for (let step = 0; step < OVERLAP_CLAMP_BINARY_STEPS; step += 1) {
    const mid = (low + high) / 2;
    if (intersects(dx * mid, dy * mid)) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return [dx * high, dy * high];
};
